/**
 * Dashboard KEV attivi (G1 / operation-notes §2.1 Fase 1).
 *
 * Lista i finding KEV (CVE knownExploited) prioritizzati per SLA residuo crescente, con badge KEV
 * e countdown delle tre soglie CRA (24h / 72h / 14gg) derivate da `detectedAt`. Punto d'ingresso
 * del percorso critico dell'analyst. Dato dalla vista globale CVE (GET /cve), filtrato lato client.
 *
 * Mostra anche l'istante dell'ultima sincronizzazione KEV (monitoring-service) e un toggle
 * "Mostra tutti i KEV" che passa dalla vista sul parco all'intero catalogo CISA KEV (GET /kev/catalog).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalCves } from '../cve/hooks.js';
import { useKevLastSync, useKevCatalog } from './hooks.js';
import {
  deriveKevSla,
  urgencyRemainingMs,
  formatRemaining,
  formatRelative,
  type SlaThreshold,
} from './sla.js';
import { ticketsCvePath } from '../triage/routes.js';
import styles from './Kev.module.css';

export function KevDashboardPage() {
  const { data, isLoading, isError } = useGlobalCves();
  const lastSync = useKevLastSync();
  const [showAll, setShowAll] = useState(false);
  const catalog = useKevCatalog(showAll);

  // Insieme dei CVE presenti sul parco (per evidenziare le voci di catalogo che ci impattano).
  const parcoSet = new Set((data ?? []).map((c) => c.cveId));

  const righe = (data ?? [])
    .filter((c) => c.knownExploited)
    .map((cve) => ({ cve, sla: cve.detectedAt ? deriveKevSla(cve.detectedAt) : null }))
    .sort((a, b) => {
      const ra = a.sla ? urgencyRemainingMs(a.sla) : Number.POSITIVE_INFINITY;
      const rb = b.sla ? urgencyRemainingMs(b.sla) : Number.POSITIVE_INFINITY;
      return ra - rb;
    });

  const catalogRows = catalog.data ?? [];

  const lastSyncLabel = lastSync.isLoading
    ? '…'
    : lastSync.isError
      ? 'non disponibile'
      : lastSync.data
        ? `${new Date(lastSync.data).toLocaleString('it-IT')} (${formatRelative(lastSync.data)})`
        : `mai`;

  return (
    <div data-testid="kev-dashboard">
      <h1 className={styles.title}>Dashboard KEV attivi</h1>
      <p className={styles.subtitle}>
        Finding KEV ordinati per SLA residuo (CRA Art.14: 24h / 72h / 14gg dalla detection).
      </p>

      <p data-testid="kev-last-sync">
        Ultimo sync KEV: <strong>{lastSyncLabel}</strong>
      </p>

      <label
        data-testid="toggle-show-all-kev-label"
        style={{ display: 'block', margin: '0.5rem 0' }}
      >
        <input
          type="checkbox"
          checked={showAll}
          onChange={(e) => setShowAll(e.target.checked)}
          data-testid="toggle-show-all-kev"
        />{' '}
        Mostra tutti i KEV (catalogo CISA completo)
      </label>

      {showAll ? (
        <>
          {catalog.isLoading && <p data-testid="kev-catalog-loading">Caricamento catalogo…</p>}
          {catalog.isError && (
            <p data-testid="kev-catalog-error">Errore nel caricamento del catalogo KEV.</p>
          )}
          {!catalog.isLoading && !catalog.isError && catalogRows.length === 0 && (
            <p data-testid="kev-catalog-vuoto">Catalogo KEV vuoto.</p>
          )}
          {catalogRows.length > 0 && (
            <table className={styles.table} data-testid="tabella-kev-catalogo">
              <thead>
                <tr>
                  <th>CVE</th>
                  <th>Vendor</th>
                  <th>Prodotto</th>
                  <th>Nome</th>
                  <th>Aggiunto</th>
                  <th>Scadenza</th>
                  <th>Su parco?</th>
                </tr>
              </thead>
              <tbody>
                {catalogRows.map((entry) => {
                  const onParco = parcoSet.has(entry.cveId);
                  return (
                    <tr key={entry.cveId} data-testid={`riga-catalogo-${entry.cveId}`}>
                      <td>
                        {onParco ? (
                          <Link
                            to={ticketsCvePath(entry.cveId)}
                            data-testid={`link-cve-${entry.cveId}`}
                          >
                            {entry.cveId}
                          </Link>
                        ) : (
                          entry.cveId
                        )}
                      </td>
                      <td>{entry.vendorProject ?? '—'}</td>
                      <td>{entry.product ?? '—'}</td>
                      <td>{entry.vulnerabilityName ?? '—'}</td>
                      <td>{entry.dateAdded ?? '—'}</td>
                      <td>{entry.dueDate ?? '—'}</td>
                      <td data-testid={`parco-${entry.cveId}`}>
                        {onParco ? <span className={styles.badgeKev}>Sì</span> : <span>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      ) : (
        <>
          {isLoading && <p data-testid="kev-loading">Caricamento…</p>}
          {isError && <p data-testid="kev-error">Errore nel caricamento dei finding KEV.</p>}
          {!isLoading && !isError && righe.length === 0 && (
            <p data-testid="kev-vuoto">Nessun finding KEV attivo.</p>
          )}

          {righe.length > 0 && (
            <table className={styles.table} data-testid="tabella-kev">
              <thead>
                <tr>
                  <th>CVE</th>
                  <th>KEV</th>
                  <th>Severità</th>
                  <th>SLA (24h / 72h / 14gg)</th>
                  <th>Release</th>
                </tr>
              </thead>
              <tbody>
                {righe.map(({ cve, sla }) => (
                  <tr key={cve.cveId} data-testid={`riga-kev-${cve.cveId}`}>
                    <td>
                      <Link to={ticketsCvePath(cve.cveId)} data-testid={`link-cve-${cve.cveId}`}>
                        {cve.cveId}
                      </Link>
                    </td>
                    <td>
                      <span className={styles.badgeKev} data-testid={`badge-kev-${cve.cveId}`}>
                        KEV
                      </span>
                    </td>
                    <td>{cve.severity ?? '—'}</td>
                    <td data-testid={`sla-${cve.cveId}`}>
                      {sla ? (
                        sla.map((t: SlaThreshold) => (
                          <span
                            key={t.label}
                            className={styles[t.status]}
                            data-testid={`sla-${cve.cveId}-${t.label}`}
                          >
                            {t.label}: {formatRemaining(t.remainingMs)}
                          </span>
                        ))
                      ) : (
                        <span>— (detection non disponibile)</span>
                      )}
                    </td>
                    <td>{cve.affectedReleasesCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
